import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fmtErr, wsUrl, avatarSrc } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Send, Shield, Flag, Eye, EyeOff, Film, Video } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { logSwallow } from "../lib/log";
import GifPicker from "../components/gifs/GifPicker";
import GifImage from "../components/gifs/GifImage";
import TranslatedText from "../components/app/TranslatedText";
import { useCall } from "../context/CallContext";

/**
 * Chat — private 1:1 messaging inside a mutual match.
 *
 * Reliability contract (this fixes the split-history bug where the SENDER's own
 * message could disappear from their side if the WebSocket echo was lost):
 *   1. Every message added to local state goes through mergeMsgs() which
 *      de-dupes by id AND merges optimistic client-generated messages
 *      (client_id) with server-confirmed messages once they arrive.
 *   2. POST /messages response is the source of truth for the sender — we push
 *      the returned message into state immediately on success, so we never
 *      depend on the WS to display our own outgoing message.
 *   3. WS `message.new` is a best-effort real-time echo. If it arrives before
 *      the HTTP response, we still merge cleanly (dedupe by id).
 *   4. Reconciler: on tab focus, on WS reconnect, and every 12s while the chat
 *      is open, we refetch GET /messages and merge — so any WS delivery that
 *      was dropped (backgrounded mobile Safari, network blip) is recovered.
 *   5. WS auto-reconnect on close/error with a small backoff.
 */

// Merge helper: keeps chronological order, dedupes by id, preserves optimistic
// stub until its server-confirmed twin arrives (matched by client_id).
function mergeMsgs(existing, incoming) {
  const byId = new Map();
  const byClientId = new Map();
  const put = (m) => {
    // Server-confirmed message with a client_id fingerprint replaces its stub
    if (m.client_id && byClientId.has(m.client_id)) {
      const stubId = byClientId.get(m.client_id);
      byId.delete(stubId);
    }
    if (byId.has(m.id)) {
      const prev = byId.get(m.id);
      byId.set(m.id, { ...prev, ...m });
    } else {
      byId.set(m.id, m);
    }
    if (m.client_id) byClientId.set(m.client_id, m.id);
  };
  existing.forEach(put);
  incoming.forEach(put);
  return Array.from(byId.values()).sort((a, b) => {
    const ka = a.created_at || "";
    const kb = b.created_at || "";
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });
}

export default function Chat() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [other, setOther] = useState(null);
  const [bothKept, setBothKept] = useState(false);
  const [youRevealed, setYouRevealed] = useState(false);
  const [theyRevealed, setTheyRevealed] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [active, setActive] = useState(true);
  const [sending, setSending] = useState(false);
  const [gifOpen, setGifOpen] = useState(false);
  const [callHint, setCallHint] = useState(false);
  const { startCall } = useCall();
  const wsRef = useRef(null);
  const wsRetryRef = useRef(null);
  const endRef = useRef(null);
  const cancelledRef = useRef(false);

  const refetchMessages = useCallback(async () => {
    try {
      const r = await api.get(`/messages/${id}`);
      setMsgs((prev) => mergeMsgs(prev, r.data.messages || []));
      setActive(r.data.active);
    } catch (e) { logSwallow("chat.refetch", e); }
  }, [id]);

  const load = useCallback(async () => {
    const m = await api.get(`/matches/${id}`);
    setOther(m.data.other_user);
    setBothKept(m.data.both_kept);
    setYouRevealed(!!m.data.you_revealed);
    setTheyRevealed(!!m.data.they_revealed);
    const r = await api.get(`/messages/${id}`);
    setMsgs((prev) => mergeMsgs(prev, r.data.messages || []));
    setActive(r.data.active);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [id]);

  // WS with auto-reconnect. Refetches on every (re)connect so no WS-dropped
  // message stays missing on either side.
  const openWs = useCallback(() => {
    if (cancelledRef.current) return;
    const url = wsUrl("/user");
    if (!url) return;
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      logSwallow("chat.ws.open", e);
      wsRetryRef.current = setTimeout(openWs, 2000);
      return;
    }
    ws.onopen = () => { refetchMessages(); };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "message.new" && msg.message?.match_id === id) {
          setMsgs((prev) => mergeMsgs(prev, [msg.message]));
          setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
        } else if (msg.type === "match.identity_revealed" && msg.match_id === id) {
          load();
        }
      } catch (e) { logSwallow("chat.ws.parse", e); }
    };
    ws.onerror = (e) => logSwallow("chat.ws.error", e);
    ws.onclose = () => {
      if (cancelledRef.current) return;
      // Retry with a small backoff. On reconnect, onopen will refetch.
      wsRetryRef.current = setTimeout(openWs, 2000);
    };
    wsRef.current = ws;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- user?.id is stable; refs guard call handlers
  }, [id, load, refetchMessages]);

  useEffect(() => {
    cancelledRef.current = false;
    (async () => {
      try { await load(); } catch (e) { toast.error(fmtErr(e)); }
      openWs();
    })();
    // Reconcile on tab focus (mobile Safari drops WS when screen locks / tab hidden)
    const onVis = () => { if (document.visibilityState === "visible") refetchMessages(); };
    document.addEventListener("visibilitychange", onVis);
    // Periodic safety net — recovers from any silent WS packet loss.
    const poll = setInterval(refetchMessages, 12000);
    return () => {
      cancelledRef.current = true;
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(poll);
      if (wsRetryRef.current) { clearTimeout(wsRetryRef.current); wsRetryRef.current = null; }
      const w = wsRef.current;
      if (w) { try { w.close(); } catch (e) { logSwallow("chat.ws.close", e); } }
      wsRef.current = null;
    };
  }, [id, load, openWs, refetchMessages]);

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || sending) return;
    // Optimistic stub — appears on the sender's screen INSTANTLY, so the user
    // never wonders "did this send?". Server response (or WS echo) will
    // replace it via mergeMsgs by client_id match.
    const clientId = `c_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const stub = {
      id: clientId,                 // temporary id — replaced by server id via client_id
      client_id: clientId,
      match_id: id,
      sender_id: user.id,
      body,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMsgs((prev) => mergeMsgs(prev, [stub]));
    setText("");
    setSending(true);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
    try {
      const { data } = await api.post("/messages", { match_id: id, body, client_id: clientId });
      const confirmed = { ...data.message, client_id: clientId, _pending: false };
      // Merge server-confirmed message; mergeMsgs will drop the stub with same client_id.
      setMsgs((prev) => mergeMsgs(prev, [confirmed]));
    } catch (e) {
      // Mark the stub failed so the user can retry. Never silently drop it.
      setMsgs((prev) => prev.map((m) => (m.id === clientId ? { ...m, _pending: false, _failed: true } : m)));
      toast.error(fmtErr(e));
    } finally {
      setSending(false);
    }
  };

  const sendGif = async (gif) => {
    if (!active || sending) return;
    const clientId = `c_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const gifFields = {
      content_type: "gif",
      gif_url: gif.gif_url,
      gif_preview_url: gif.gif_preview_url,
      gif_id: gif.gif_id,
      gif_slug: gif.gif_slug,
      gif_width: gif.gif_width,
      gif_height: gif.gif_height,
      gif_source: "klipy",
    };
    const stub = {
      id: clientId,
      client_id: clientId,
      match_id: id,
      sender_id: user.id,
      body: "",
      ...gifFields,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMsgs((prev) => mergeMsgs(prev, [stub]));
    setSending(true);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
    try {
      const { data } = await api.post("/messages", {
        match_id: id,
        content_type: "gif",
        client_id: clientId,
        gif: {
          gif_url: gif.gif_url,
          gif_preview_url: gif.gif_preview_url,
          gif_id: gif.gif_id,
          gif_slug: gif.gif_slug,
          gif_width: gif.gif_width,
          gif_height: gif.gif_height,
        },
      });
      const confirmed = { ...data.message, client_id: clientId, _pending: false };
      setMsgs((prev) => mergeMsgs(prev, [confirmed]));
    } catch (e) {
      setMsgs((prev) => prev.map((m) => (m.id === clientId ? { ...m, _pending: false, _failed: true } : m)));
      toast.error(fmtErr(e));
    } finally {
      setSending(false);
    }
  };

  const keep = async () => {
    try {
      const { data } = await api.post("/matches/keep", { match_id: id });
      if (data.match.kept_by_a && data.match.kept_by_b) {
        toast.success("Kept. Added to Vault.");
        setBothKept(true);
      } else {
        toast("You marked Keep. Waiting on them.");
      }
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const reveal = async () => {
    if (youRevealed) return;
    const ok = window.confirm(
      "Reveal your real identity (name, photo, age, hometown) to this match?\n\nThis is permanent for this match."
    );
    if (!ok) return;
    try {
      await api.post(`/matches/${id}/reveal`);
      setYouRevealed(true);
      toast.success("Identity revealed to this match");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const block = async () => {
    if (!other) return;
    try { await api.post("/blocks", { target_id: other.id }); toast("Blocked."); nav("/scan"); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const report = async () => {
    if (!other) return;
    const reason = window.prompt("Report reason?");
    if (!reason) return;
    try { await api.post("/reports", { target_id: other.id, reason, context: `match:${id}` }); toast("Report sent."); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const otherDisplayName = other?.real_name || other?.display_name || "…";
  const inGhost = (user?.mode_default || "anonymous") !== "real";
  const canCall = youRevealed && theyRevealed && active;

  // One-time discovery hint the first time calling becomes available on this match.
  useEffect(() => {
    if (!canCall) return;
    const key = `bl_call_hint_${id}`;
    try {
      if (!localStorage.getItem(key)) {
        setCallHint(true);
        localStorage.setItem(key, "1");
        const tm = setTimeout(() => setCallHint(false), 7000);
        return () => clearTimeout(tm);
      }
    } catch (e) { logSwallow("chat.callhint", e); }
  }, [canCall, id]);

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col max-w-md mx-auto">
      <header className="px-4 py-3 border-b border-white/5 flex items-center gap-3">
        <Link to="/scan" data-testid="chat-back"><ArrowLeft size={20}/></Link>
        {avatarSrc(other) && (
          <img src={avatarSrc(other)} alt="" className="w-8 h-8 rounded-full object-cover border border-white/10" data-testid="chat-other-avatar" />
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate" data-testid="chat-other-name">{otherDisplayName}</div>
          <div className="text-xs text-zinc-500 font-mono-accent flex items-center gap-1">
            {theyRevealed ? <Eye size={11} className="text-[#22C55E]" /> : <EyeOff size={11} className="text-zinc-500" />}
            <span>{active ? (bothKept ? "kept · permanent" : "ephemeral · 12h") : "expired"}</span>
          </div>
        </div>
        {inGhost && !youRevealed && (
          <button onClick={reveal} data-testid="chat-reveal-btn" title="Reveal my real identity to this match" className="text-xs font-mono-accent text-zinc-400 hover:text-white">REVEAL</button>
        )}
        {canCall && (
          <button onClick={() => startCall(id, otherDisplayName)} data-testid="chat-call-btn" title="Start a video/voice call"
            className="text-zinc-300 hover:text-[#22C55E] transition-colors"><Video size={18}/></button>
        )}
        <button onClick={keep} disabled={bothKept} data-testid="keep-btn" className="text-xs font-mono-accent text-[#E60000] disabled:text-zinc-600">{bothKept ? "KEPT" : "KEEP"}</button>
        <button onClick={report} data-testid="report-btn" className="text-zinc-500 hover:text-white"><Flag size={16}/></button>
        <button onClick={block} data-testid="block-btn" className="text-zinc-500 hover:text-white"><Shield size={16}/></button>
      </header>

      {callHint && (
        <div className="px-4 py-2.5 bg-[#22C55E]/10 border-b border-[#22C55E]/20 text-center text-xs text-[#7CE0A0] font-mono-accent animate-pulse" data-testid="call-available-hint">
          {t("call.availableHint")}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2" data-testid="messages">
        {msgs.length === 0 && <p className="text-zinc-500 text-center text-sm mt-10">Say hi. It&apos;s just you two.</p>}
        {msgs.map((m) => {
          const mine = m.sender_id === user.id;
          const pending = m._pending;
          const failed = m._failed;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
              <div
                className={`max-w-[78%] px-4 py-2 rounded-2xl ${
                  mine
                    ? failed
                      ? "bg-[#4a0000] text-white border border-[#E60000]/50"
                      : pending
                      ? "bg-[#7a0000] text-white/90"
                      : "bg-[#E60000] text-white"
                    : "bg-[#1A1A1A] text-white"
                }`}
              >
                {m.content_type === "gif" && m.gif_url ? (
                  <GifImage src={m.gif_url} maxHeight={260} testId={`msg-gif-${m.id}`} className="w-auto" />
                ) : (
                  <TranslatedText text={m.body} testId={`msg-tx-${m.id}`} />
                )}
                {failed && <span className="ml-2 text-xs text-[#FFB4B4] font-mono-accent">· FAILED</span>}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="border-t border-white/5 p-3 flex gap-2" data-testid="chat-form">
        <button
          type="button"
          onClick={() => setGifOpen(true)}
          disabled={!active}
          data-testid="chat-gif-btn"
          title="Send a GIF"
          className="bg-[#1A1A1A] hover:bg-[#242424] disabled:opacity-30 rounded-full px-3 flex items-center gap-1 text-zinc-300 transition"
        >
          <Film size={16} /> <span className="text-[11px] font-mono-accent">GIF</span>
        </button>
        <input
          data-testid="chat-input"
          value={text} onChange={(e) => setText(e.target.value)}
          disabled={!active}
          placeholder={active ? t("chat.message") : t("chat.expired")}
          className="flex-1 bg-[#1A1A1A] rounded-full px-4 py-3 outline-none"
        />
        <button
          data-testid="chat-send"
          disabled={!active || !text.trim() || sending}
          className="bg-[#E60000] hover:bg-[#FF1A1A] disabled:opacity-30 rounded-full p-3 transition"
        >
          <Send size={18}/>
        </button>
      </form>

      <GifPicker open={gifOpen} onClose={() => setGifOpen(false)} onSelect={sendGif} />
    </div>
  );
}
